import { HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo } from '@azure/msal-browser';
import { from, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { CoreAuthConfig } from './core-auth.models';
import { CoreAuthTokenService } from './core-auth.token.service';
import { CORE_AUTH_CONFIG } from './core-auth.tokens';

@Injectable()
export class CoreAuthInterceptor implements HttpInterceptor {
  constructor(
    private readonly msalService: MsalService,
    private readonly tokenService: CoreAuthTokenService,
    @Inject(CORE_AUTH_CONFIG) private readonly config: CoreAuthConfig,
  ) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const shouldAuthorize = this.canAuthorize(req.url);

    if (!shouldAuthorize || req.headers.has('Authorization')) {
      return next.handle(this.appendSubscriptionKeyIfNeeded(req, shouldAuthorize));
    }

    const cachedToken = this.tokenService.getBearerToken();
    if (cachedToken) {
      return next.handle(this.appendAuthorization(req, cachedToken, shouldAuthorize));
    }

    const account = this.getAccount();
    if (!account) {
      return next.handle(this.appendSubscriptionKeyIfNeeded(req, shouldAuthorize));
    }

    return from(
      this.msalService.acquireTokenSilent({
        account,
        scopes: this.config.msal.scopes ?? [],
      }),
    ).pipe(
      switchMap((result) => {
        const token = result.accessToken || this.tokenService.getBearerToken();
        const request = token
          ? this.appendAuthorization(req, token, shouldAuthorize)
          : this.appendSubscriptionKeyIfNeeded(req, shouldAuthorize);
        return next.handle(request);
      }),
    );
  }

  private appendAuthorization(
    req: HttpRequest<unknown>,
    token: string,
    shouldAuthorize: boolean,
  ): HttpRequest<unknown> {
    let headers: HttpHeaders = req.headers.append('Authorization', `Bearer ${token}`);

    if (shouldAuthorize && this.config.api.subscriptionKey && !headers.has('Ocp-Apim-Subscription-Key')) {
      headers = headers.append('Ocp-Apim-Subscription-Key', this.config.api.subscriptionKey);
    }

    return req.clone({ headers });
  }

  private appendSubscriptionKeyIfNeeded(req: HttpRequest<unknown>, shouldAuthorize: boolean): HttpRequest<unknown> {
    if (!shouldAuthorize || !this.config.api.subscriptionKey || req.headers.has('Ocp-Apim-Subscription-Key')) {
      return req;
    }

    return req.clone({
      headers: req.headers.append('Ocp-Apim-Subscription-Key', this.config.api.subscriptionKey),
    });
  }

  private getAccount(): AccountInfo | null {
    const activeAccount = this.msalService.instance.getActiveAccount();
    if (activeAccount) {
      return activeAccount;
    }

    const allAccounts = this.msalService.instance.getAllAccounts();
    if (!allAccounts.length) {
      return null;
    }

    this.msalService.instance.setActiveAccount(allAccounts[0]);
    return allAccounts[0];
  }

  private canAuthorize(url: string): boolean {
    const whiteList = this.config.api.whitelistPatterns ?? [];
    if (whiteList.some((item) => url.includes(item))) {
      return false;
    }

    const patterns = this.config.api.protectedResourcePatterns ?? [];
    if (!patterns.length) {
      return true;
    }

    return patterns.some((pattern) => url.startsWith(pattern) || url.includes(pattern));
  }
}
